import { FC, useEffect, useRef } from "react";
import { WithChildren } from "../../../../_metronic/helpers";
import { useAuth } from "./Auth";
import { requestReverify } from "./AuthHelpers";
import { reverifyLock } from "./_requests";
import {
  claimIdleLock,
  isIdle,
  lastActivityAt,
  markActivity,
  refreshIdleClaim,
  releaseIdleLock,
  subscribeActivity,
} from "./idle";

/**
 * Замок панели по простою человека.
 *
 * Бэк сам такого простоя не увидит: окно активности продлевает ЛЮБОЙ запрос
 * сессии, а панель ходит в API в фоне — карточки сделок и транзакций раз в 15
 * секунд, плюс refetchInterval у половины разделов. Пока вкладка открыта, окно
 * не закрывалось никогда, и кода не спрашивали ни разу. Кто человек, а кто
 * таймер, знает только фронт — поэтому запирает он, а бэк остаётся местом,
 * где замок проверяется (см. FRONTEND_ADMIN_REVERIFY.md в garant-backend).
 *
 * Порядок такой: заперли сессию ручкой → сразу показали окно с кодом. Второе
 * важно не меньше первого: без него человек вернулся бы к рабочей с виду
 * панели и упёрся в код на первом же клике, посреди недописанной формы.
 */

/**
 * Как часто сверяемся с простоем.
 *
 * Тридцати секунд хватает: в скрытой вкладке браузер всё равно прижимает
 * таймеры примерно до одного срабатывания в минуту, а окно — пятнадцать минут.
 */
const CHECK_INTERVAL_MS = 30 * 1000;

const IdleLockProvider: FC<WithChildren> = ({ children }) => {
  const { isAuth } = useAuth();
  // Эта вкладка сейчас запирает сессию и держит окно с кодом.
  const locking = useRef(false);

  useEffect(() => {
    if (!isAuth) return;

    // Счётчика нет вовсе — панель открыли впервые в этом браузере, человек
    // только что пришёл. Непустой не трогаем: он общий на все вкладки, и
    // перезагрузка страницы после часа отсутствия — не повод начинать отсчёт
    // заново, а как раз повод спросить код.
    if (!lastActivityAt()) markActivity();

    const stopActivity = subscribeActivity(() => markActivity());

    const check = async () => {
      // Окно с кодом держит эта вкладка — продлеваем заявку, чтобы соседние не
      // решили, что мы умерли, и не заказали второй код.
      if (locking.current) {
        refreshIdleClaim();
        return;
      }

      if (!isIdle()) return;
      // Замок ставит одна вкладка из всех: иначе в Telegram уедет пачка кодов.
      if (!claimIdleLock()) return;

      locking.current = true;
      try {
        const res = await reverifyLock();
        if (res.status !== 200) {
          // Не заперли (сеть, 401 после протухшей сессии) — отпускаем заявку,
          // попробуем на следующем тике.
          releaseIdleLock();
          return;
        }

        const verified = await requestReverify();
        // Подтвердился — отсчёт с нуля: ввод кода это тоже работа в панели.
        if (verified) markActivity();
      } catch {
        releaseIdleLock();
      } finally {
        locking.current = false;
      }
    };

    const timer = window.setInterval(check, CHECK_INTERVAL_MS);
    // Вернулись к вкладке — проверяем сразу, чтобы код спросили до первого
    // клика, а не после него.
    document.addEventListener("visibilitychange", check);
    window.addEventListener("focus", check);
    check();

    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", check);
      window.removeEventListener("focus", check);
      stopActivity();
    };
  }, [isAuth]);

  return <>{children}</>;
};

export { IdleLockProvider };
